import type { TokenResponse } from '@/types'

const AUTH_URL = import.meta.env.VITE_AUTH_URL as string
const API_URL = import.meta.env.VITE_API_URL as string

const TOKEN_KEY = 'rentflow_access_token'
const REFRESH_KEY = 'rentflow_refresh_token'
const VERIFIER_KEY = 'rentflow_code_verifier'
const STATE_KEY = 'rentflow_oauth_state'

const CLIENT_ID = 'rentflow-web'
const REDIRECT_URI = `${window.location.origin}/callback`
const SCOPES = 'profile profile:write account account:write properties properties:write offline_access'

export function getAccessToken(): string | null {
	return localStorage.getItem(TOKEN_KEY)
}

export function getRefreshToken(): string | null {
	return localStorage.getItem(REFRESH_KEY)
}

export function setTokens(accessToken: string, refreshToken: string): void {
	localStorage.setItem(TOKEN_KEY, accessToken)
	localStorage.setItem(REFRESH_KEY, refreshToken)
}

export function clearTokens(): void {
	localStorage.removeItem(TOKEN_KEY)
	localStorage.removeItem(REFRESH_KEY)
	localStorage.removeItem(VERIFIER_KEY)
	localStorage.removeItem(STATE_KEY)
}

export function isAuthenticated(): boolean {
	const token = getAccessToken()
	if (!token) return false

	try {
		const payload = JSON.parse(atob(token.split('.')[1])) as { exp: number }
		return payload.exp * 1000 > Date.now()
	} catch {
		return false
	}
}

export function getUserIdFromToken(): string | null {
	const token = getAccessToken()
	if (!token) return null

	try {
		const payload = JSON.parse(atob(token.split('.')[1])) as { sub: string }
		return payload.sub
	} catch {
		return null
	}
}

async function generatePKCE(): Promise<{ codeVerifier: string; codeChallenge: string }> {
	const array = new Uint8Array(32)
	crypto.getRandomValues(array)
	const codeVerifier = btoa(String.fromCharCode(...array))
		.replace(/\+/g, '-')
		.replace(/\//g, '_')
		.replace(/=+$/, '')

	const encoder = new TextEncoder()
	const data = encoder.encode(codeVerifier)
	const digest = await crypto.subtle.digest('SHA-256', data)
	const codeChallenge = btoa(String.fromCharCode(...new Uint8Array(digest)))
		.replace(/\+/g, '-')
		.replace(/\//g, '_')
		.replace(/=+$/, '')

	return { codeVerifier, codeChallenge }
}

export async function redirectToAuth(): Promise<never> {
	const { codeVerifier, codeChallenge } = await generatePKCE()
	const state = crypto.randomUUID()

	localStorage.setItem(VERIFIER_KEY, codeVerifier)
	localStorage.setItem(STATE_KEY, state)

	const params = new URLSearchParams({
		client_id: CLIENT_ID,
		redirect_uri: REDIRECT_URI,
		response_type: 'code',
		state,
		code_challenge: codeChallenge,
		code_challenge_method: 'S256',
		scope: SCOPES
	})

	window.location.href = `${AUTH_URL}/authorize?${params.toString()}`
	return new Promise<never>(() => {})
}

export async function exchangeCode(code: string, state: string): Promise<boolean> {
	const savedState = localStorage.getItem(STATE_KEY)
	const codeVerifier = localStorage.getItem(VERIFIER_KEY)

	if (!savedState || savedState !== state || !codeVerifier) {
		console.error('[auth] exchangeCode: state/verifier mismatch', { savedState, state, hasVerifier: !!codeVerifier })
		return false
	}

	const res = await fetch(`${API_URL}/oauth/token`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({
			grant_type: 'authorization_code',
			code,
			code_verifier: codeVerifier,
			client_id: CLIENT_ID,
			redirect_uri: REDIRECT_URI
		})
	})

	if (!res.ok) {
		const body = await res.json().catch(() => ({}))
		console.error('[auth] exchangeCode: token exchange failed', res.status, body)
		return false
	}

	const data = (await res.json()) as TokenResponse
	setTokens(data.access_token, data.refresh_token)

	localStorage.removeItem(VERIFIER_KEY)
	localStorage.removeItem(STATE_KEY)

	return true
}

export async function tryRefreshTokens(): Promise<boolean> {
	const refreshToken = getRefreshToken()
	if (!refreshToken) return false

	try {
		const res = await fetch(`${API_URL}/auth/refresh`, {
			method: 'POST',
			credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ refresh_token: refreshToken }),
		})
		if (!res.ok) return false
		const data = (await res.json()) as { access_token?: string; accessToken?: string; refresh_token?: string }
		// accept both snake_case (after backend fix) and camelCase (current backend)
		const newAccess = data.access_token ?? data.accessToken
		const newRefresh = data.refresh_token ?? refreshToken
		if (!newAccess) return false
		setTokens(newAccess, newRefresh)
		return true
	} catch {
		return false
	}
}

export async function logout(): Promise<void> {
	try {
		await fetch(`${API_URL}/auth/logout`, {
			method: 'POST',
			credentials: 'include',
		})
	} catch (err) {
		console.error('[auth] logout: server cleanup failed (continuing)', err)
	}
	clearTokens()
	window.location.href = '/'
}
